import { Box, Grid, Tab, Tabs } from "@mui/material";
import { SyntheticEvent, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TabPanel } from "../../common/components/functional/tabpanel/TabPanel";
import { AccessRightContext } from "../user/AccessRightProvider";
import { AccountingMainPage } from "./AccountingMainPage";
import UnionsPanel from "./Unions/UnionsPanel";
import { ExpertsControlPanel } from "./experts/ExpertsControlPanel";
import { OrderQueuesPanel } from "./orderQueues/OrderQueuesPanel";
import { SchedulingPeriodsControlPanel } from "./salaryAndScheduling/SchedulingPeriodsControlPanel";
import { SupplementPricing } from "./supplementPricing/SupplementPricing";
import TesSalaryTablesControlPanel from "./tesSalaryTables/TesSalaryTablesControlPanel";
import { TestingUtilities } from "./testingUtilities/TestingUtilities";
import { TalentsControlPanel } from "./talents/TalentsControlPanel";
import { useNavigate } from "react-router-dom";
import BulkSalaryIncreasePage from "./bulkSalaryIncrease/BulkSalaryIncreasePage";
import { RecruitmentAreasPanel } from "./recruitment/RecruitmentAreasPanel";
import { AnnouncementsPanel } from "./announcements/AnnouncementsPanel";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export enum SettingsTabName {
  ExpenseTypesAndAccounts = "expenseTypesAndAccounts",
  SalaryAndSchedulingPeriods = "salaryAndSchedulingPeriods",
  OrderQueues = "orderQueues",
  RecruitmentAreas = "recruitmentAreas",
  Unions = "unions",
  SupplementPricing = "supplementPricing",
  Experts = "experts",
  Talents = "talents",
  TestingTools = "testingTools",
  BulkSalaryIncrease = "bulkSalaryIncreaseTab",
  TesSalaryTables = "tesSalaryTables",
  Announcements = "announcements",
}

export const AdminSettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const { isAdmin } = useContext(AccessRightContext);

  const [tabIndex, setTabIndex] = useState(0);

  const tabValues = useMemo(() => {
    return [
      {
        name: SettingsTabName.ExpenseTypesAndAccounts,
        content: <AccountingMainPage />,
      },
      {
        name: SettingsTabName.SalaryAndSchedulingPeriods,
        content: <SchedulingPeriodsControlPanel />,
      },
      { name: SettingsTabName.OrderQueues, content: <OrderQueuesPanel /> },
      {
        name: SettingsTabName.RecruitmentAreas,
        content: <RecruitmentAreasPanel />,
      },
      { name: SettingsTabName.Unions, content: <UnionsPanel /> },
      {
        name: SettingsTabName.SupplementPricing,
        content: <SupplementPricing />,
      },
      { name: SettingsTabName.Experts, content: <ExpertsControlPanel /> },
      { name: SettingsTabName.Talents, content: <TalentsControlPanel /> },
      { name: SettingsTabName.TestingTools, content: <TestingUtilities /> },
      {
        name: SettingsTabName.BulkSalaryIncrease,
        content: <BulkSalaryIncreasePage />,
      },
      {
        name: SettingsTabName.TesSalaryTables,
        content: <TesSalaryTablesControlPanel />,
      },
      { name: SettingsTabName.Announcements, content: <AnnouncementsPanel /> },
    ];
  }, []);

  const handleTabChange = (_: SyntheticEvent, newTabIndex: number) => {
    navigate(`/home/settings/${tabValues[newTabIndex].name}`);
    setTabIndex(newTabIndex);
  };

  return isAdmin ? (
    <Box
      data-testid="settings-page-root"
      sx={{ width: 0, minWidth: "100%", overflow: "hidden" }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{
              width: "100%",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
              },
              "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3,
              },
            }}
          >
            {tabValues.map((tab) => (
              <Tab
                key={tab.name}
                label={t(`settings:${tab.name}`)}
                {...a11yProps(tabValues.indexOf(tab))}
              />
            ))}
          </Tabs>
          {tabValues.map((tab) => (
            <TabPanel
              key={tab.name}
              value={tabIndex}
              index={tabValues.indexOf(tab)}
            >
              {tab.content}
            </TabPanel>
          ))}
        </Grid>
      </Grid>
    </Box>
  ) : null;
};
